import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ATTRIBUTE } from '@/constants/attributes';

export const ALL = 'all';

export const COMBINATIONS = 'combinations';

export const DELETED = 'deleted';

export const INFLUENCE = 'influence';

export const INVALID = 'invalid';

export const RETAIN = 'retain';

export const VARIANTS = 'variants';

export const VARIATIONS_TYPE = {
  BULK_CONTROLS: 'bulk-controls',
  BULK_INDIVIDUAL: 'bulk-individual',
  SINGLE: 'single',
  PROFILES: 'profiles',
};

export const MAXIMUM_OPTIONS_TO_SHOW = 4;

export const THUMBNAIL_SIZE = 42;

export const NO_VARIANT = [
  'No first variation',
  'No second variation',
  'No third variation',
  'No fourth variation',
  'No fifth variation',
];

export const NUMBER_OF_VARIANTS = {
  [EBAY]: 5,
  [ETSY]: 2,
  [FAIRE]: 3,
  [SHOPIFY]: 3,
};

export const CHANNEL_VARIANTS = {
  [EBAY]: [...Array(NUMBER_OF_VARIANTS[EBAY]).keys()],
  [ETSY]: [...Array(NUMBER_OF_VARIANTS[ETSY]).keys()],
  [FAIRE]: [...Array(NUMBER_OF_VARIANTS[FAIRE]).keys()],
  [SHOPIFY]: [...Array(NUMBER_OF_VARIANTS[SHOPIFY]).keys()],
};

export const TAB = {
  AVAILABILITY: 'Availability',
  BARCODE: 'Barcode',
  GTIN: 'GTIN',
  PRICE: 'Price',
  QUANTITY: 'Quantity',
  PHOTOS: 'Photos',
  PROCESSING: 'Processing',
  SHIPPING: 'Shipping',
  SKU: 'SKU',
  VARIATIONS: 'Variations',
  VISIBILITY: 'Visibility',
};

export const TAB_TO_ATTRIBUTE = {
  [TAB.AVAILABILITY]: ATTRIBUTE.PREORDER,
  [TAB.BARCODE]: ATTRIBUTE.BARCODE,
  [TAB.GTIN]: ATTRIBUTE.GTIN,
  [TAB.PRICE]: ATTRIBUTE.PRICE,
  [TAB.PROCESSING]: ATTRIBUTE.PROCESSING_PROFILE,
  [TAB.QUANTITY]: ATTRIBUTE.QUANTITY,
  [TAB.PHOTOS]: ATTRIBUTE.PHOTOS,
  [TAB.SHIPPING]: ATTRIBUTE.SHIPPING,
  [TAB.SKU]: ATTRIBUTE.SKU,
  [TAB.VISIBILITY]: ATTRIBUTE.VISIBILITY,
};

export const TAB_VALUES = {
  [EBAY]: {
    [TAB.PRICE]: [ATTRIBUTE.PRICE],
    [TAB.QUANTITY]: [ATTRIBUTE.QUANTITY],
    [TAB.SKU]: [ATTRIBUTE.SKU],
  },
  [ETSY]: {
    [TAB.PRICE]: [ATTRIBUTE.PRICE],
    [TAB.PROCESSING]: [ATTRIBUTE.PROCESSING_PROFILE],
    [TAB.QUANTITY]: [ATTRIBUTE.QUANTITY],
    [TAB.SKU]: [ATTRIBUTE.SKU],
  },
  [FAIRE]: {
    [TAB.AVAILABILITY]: [ATTRIBUTE.PREORDER, ATTRIBUTE.START_DATE, ATTRIBUTE.END_DATE, ATTRIBUTE.DEADLINE_DATE],
    [TAB.GTIN]: [ATTRIBUTE.GTIN],
    [TAB.PRICE]: [ATTRIBUTE.RETAIL_PRICE, ATTRIBUTE.WHOLESALE_PRICE],
    [TAB.QUANTITY]: [ATTRIBUTE.QUANTITY, ATTRIBUTE.RESTOCK_DATE],
    [TAB.SHIPPING]: [ATTRIBUTE.TARIFF_CODE, ATTRIBUTE.WEIGHT, ATTRIBUTE.LENGTH, ATTRIBUTE.WIDTH, ATTRIBUTE.HEIGHT],
    [TAB.SKU]: [ATTRIBUTE.SKU],
  },
  [SHOPIFY]: {
    [TAB.BARCODE]: [ATTRIBUTE.BARCODE],
    [TAB.PRICE]: [ATTRIBUTE.CHARGE_TAX, ATTRIBUTE.PRICE, ATTRIBUTE.CAP, ATTRIBUTE.CPI],
    [TAB.QUANTITY]: [ATTRIBUTE.CONTINUE_SELLING, ATTRIBUTE.TRACK_QUANTITY, ATTRIBUTE.QUANTITY],
    [TAB.SKU]: [ATTRIBUTE.SKU],
    [TAB.SHIPPING]: [ATTRIBUTE.PHYSICAL, ATTRIBUTE.WEIGHT, ATTRIBUTE.COUNTRY_CODE, ATTRIBUTE.HS_CODE],
    [TAB.VISIBILITY]: [DELETED],
  },
};

export const INFLUENCE_ATTRIBUTES = {
  [ETSY]: [
    ATTRIBUTE.PRICE,
    ATTRIBUTE.PROCESSING_PROFILE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
  ],
};

export const INFLUENCE_CONTROLS = {
  [ETSY]: {
    [TAB.PRICE]: true,
    [TAB.PROCESSING]: true,
    [TAB.QUANTITY]: true,
    [TAB.SKU]: true,
  },
};

export const INFLUENCE_CONTROLS_LABEL = {
  [TAB.PRICE]: 'Individual price',
  [TAB.PROCESSING]: 'Individual PP',
  [TAB.QUANTITY]: 'Individual quantity',
  [TAB.SKU]: 'Individual SKU',
};

export const VALIDATIONS = {
  COMBINATIONS: {
    MAXIMUM_NUMBER: {
      [EBAY]: 250,
      [ETSY]: 400,
      [SHOPIFY]: parseInt(import.meta.env.VELA_SHOPIFY_MAX_VARIATION_COMBINATIONS, 10) || 100,
    },
  },
  OPTIONS: {
    MAXIMUM_NAME_LENGTH: {
      [EBAY]: 65,
      [ETSY]: 255,
      [SHOPIFY]: 255,
    },
    MAXIMUM_NUMBER: {
      [EBAY]: 50,
      [ETSY]: 70,
      [SHOPIFY]: parseInt(import.meta.env.VELA_SHOPIFY_MAX_VARIATION_COMBINATIONS, 10) || 100,
    },
  },
  PHOTOS: {
    MAXIMUM_NUMBER: {
      [EBAY]: 12,
    },
  },
  VARIANTS: {
    MAXIMUM_NAME_LENGTH: {
      [EBAY]: 65,
      [ETSY]: 45,
      [SHOPIFY]: 255,
    },
  },
};

export const ERRORS = {
  COMBINATIONS: {
    NO_VISIBLE: 'At least one combination must be visible',
    NO_STOCK: 'At least one combination must be in stock',
    NUMBER_EXCEEDED: {
      [EBAY]: `Should not exceed ${VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER[EBAY]} options combinations`,
      [ETSY]: `Should not exceed ${VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER[ETSY]} options combinations`,
      [SHOPIFY]: `Should not exceed ${VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER[SHOPIFY]} options combinations`,
    },
    SKU_NON_UNIQUE: 'SKU must be unique',
  },
  OPTIONS: {
    FORBIDDEN_CHARACTERS: {
      [ETSY]: 'You may not include any of these characters: ^ $ `',
    },
    NAME_EXIST: 'You may not have two options with same name',
    NOT_ALLOWED: 'You may not have option with this name',
    NAME_LENGTH_EXCEEDED: {
      [EBAY]: `Option name should not exceed ${VALIDATIONS.OPTIONS.MAXIMUM_NAME_LENGTH[EBAY]} characters`,
      [ETSY]: `Option name should not exceed ${VALIDATIONS.OPTIONS.MAXIMUM_NAME_LENGTH[ETSY]} characters`,
      [SHOPIFY]: `Option name should not exceed ${VALIDATIONS.OPTIONS.MAXIMUM_NAME_LENGTH[SHOPIFY]} characters`,
    },
    NO_OPTION: 'Fill in option',
    NO_OPTIONS: 'Add at least one option',
    NUMBER_EXCEEDED: {
      [EBAY]: `Should not exceed ${VALIDATIONS.OPTIONS.MAXIMUM_NUMBER[EBAY]} options`,
      [ETSY]: `Should not exceed ${VALIDATIONS.OPTIONS.MAXIMUM_NUMBER[ETSY]} options`,
      [SHOPIFY]: `Should not exceed ${VALIDATIONS.OPTIONS.MAXIMUM_NUMBER[SHOPIFY]} options`,
    },
  },
  VARIANTS: {
    FORBIDDEN_CHARACTERS: {
      [ETSY]: 'You may not include any of these characters: ^ $ `',
      [SHOPIFY]: 'You may not include the character sequence " / "',
    },
    NAME_EXIST: 'You may not have two variations with same name',
    NAME_FORBIDDEN: 'You may not have variation with this name',
    NAME_LENGTH_EXCEEDED: {
      [EBAY]: `Variant name should not exceed ${VALIDATIONS.VARIANTS.MAXIMUM_NAME_LENGTH[EBAY]} characters`,
      [ETSY]: `Variant name should not exceed ${VALIDATIONS.VARIANTS.MAXIMUM_NAME_LENGTH[ETSY]} characters`,
      [SHOPIFY]: `Variant name should not exceed ${VALIDATIONS.VARIANTS.MAXIMUM_NAME_LENGTH[SHOPIFY]} characters`,
    },
    NAME_REQUIRED: 'Fill in variation name',
    NUMBER_EXCEEDED: {
      [EBAY]: `Should not exceed ${NUMBER_OF_VARIANTS[EBAY]} variations`,
      [ETSY]: `Should not exceed ${NUMBER_OF_VARIANTS[ETSY]} variations`,
      [SHOPIFY]: `Should not exceed ${NUMBER_OF_VARIANTS[SHOPIFY]} variations`,
    },
    SCALE: 'Choose scale',
  },
};

export const PLACEHOLDERS = {
  [DELETED]: 'Visibility',
  [ATTRIBUTE.BARCODE]: 'Barcode',
  [ATTRIBUTE.CAP]: 'CAP',
  [ATTRIBUTE.CHARGE_TAX]: 'Charge tax',
  [ATTRIBUTE.CONTINUE_SELLING]: 'Cont selling',
  [ATTRIBUTE.COUNTRY_CODE]: 'Country of origin',
  [ATTRIBUTE.CPI]: 'CPI',
  [ATTRIBUTE.DEADLINE_DATE]: 'Deadline to order',
  [ATTRIBUTE.END_DATE]: 'End ship date',
  [ATTRIBUTE.GTIN]: 'GTIN',
  [ATTRIBUTE.HEIGHT]: 'Height',
  [ATTRIBUTE.HS_CODE]: 'HS Code',
  [ATTRIBUTE.LENGTH]: 'Length',
  [ATTRIBUTE.PHYSICAL]: 'Physical',
  [ATTRIBUTE.PREORDER]: 'Preorder',
  [ATTRIBUTE.PRICE]: 'Price',
  [ATTRIBUTE.PROCESSING_PROFILE]: 'Processing profile',
  [ATTRIBUTE.QUANTITY]: 'Quantity',
  [ATTRIBUTE.RESTOCK_DATE]: 'Restock date',
  [ATTRIBUTE.SKU]: 'SKU',
  [ATTRIBUTE.START_DATE]: 'Start ship date',
  [ATTRIBUTE.TARIFF_CODE]: 'Tariff code',
  [ATTRIBUTE.TRACK_QUANTITY]: 'Track inv',
  [ATTRIBUTE.WEIGHT]: 'Weight',
  [ATTRIBUTE.WIDTH]: 'Width',
  ADD_VARIATION: 'Add Variation',
  CHOOSE_VARIATION: 'Choose Variation',
  OPTION: 'Add option',
  SECTION: 'Choose section',
  SCALE: 'Choose Scale',
};

export const RETAIN_ATTRIBUTES = {
  [EBAY]: [
    ATTRIBUTE.PRICE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
  ],
  [ETSY]: [
    ATTRIBUTE.PRICE,
    ATTRIBUTE.PROCESSING_PROFILE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
  ],
  [FAIRE]: [
    ATTRIBUTE.GTIN,
    ATTRIBUTE.PREORDER,
    ATTRIBUTE.PRICE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.SHIPPING,
  ],
  [SHOPIFY]: [
    ATTRIBUTE.BARCODE,
    ATTRIBUTE.PRICE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.SHIPPING,
  ],
};

export const COMBINATION_ATTRIBUTES_TO_RETAIN = {
  [EBAY]: [
    ATTRIBUTE.PRICE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
  ],
  [FAIRE]: [
    'imageUrl',
    ATTRIBUTE.DEADLINE_DATE,
    ATTRIBUTE.DISTANCE_UNIT,
    ATTRIBUTE.END_DATE,
    ATTRIBUTE.GTIN,
    ATTRIBUTE.HEIGHT,
    ATTRIBUTE.LENGTH,
    ATTRIBUTE.MASS_UNIT,
    ATTRIBUTE.PREORDER,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.RESTOCK_DATE,
    ATTRIBUTE.RETAIL_PRICE,
    ATTRIBUTE.SKU,
    ATTRIBUTE.START_DATE,
    ATTRIBUTE.TARIFF_CODE,
    ATTRIBUTE.WEIGHT,
    ATTRIBUTE.WHOLESALE_PRICE,
    ATTRIBUTE.WIDTH,
  ],
  [SHOPIFY]: [
    'imageHash',
    'imageId',
    ATTRIBUTE.BARCODE,
    ATTRIBUTE.CAP,
    ATTRIBUTE.CHARGE_TAX,
    ATTRIBUTE.CONTINUE_SELLING,
    ATTRIBUTE.COUNTRY_CODE,
    ATTRIBUTE.CPI,
    ATTRIBUTE.HS_CODE,
    ATTRIBUTE.PHYSICAL,
    ATTRIBUTE.PRICE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.TRACK_QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.UNIT,
    ATTRIBUTE.WEIGHT,
  ],
};

export const ATTRIBUTES_TO_VALIDATE = {
  [EBAY]: [
    ATTRIBUTE.PRICE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
  ],
  [ETSY]: [
    ATTRIBUTE.PRICE,
    ATTRIBUTE.PROCESSING_PROFILE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.VISIBILITY,
  ],
  [FAIRE]: [
    ATTRIBUTE.RETAIL_PRICE,
    ATTRIBUTE.WHOLESALE_PRICE,
  ],
  [SHOPIFY]: [
    ATTRIBUTE.PRICE,
    ATTRIBUTE.CAP,
    ATTRIBUTE.CPI,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.BARCODE,
    ATTRIBUTE.WEIGHT,
  ],
};

export const BULK_OPERATION = {
  ADD_AFTER: 'addAfter',
  ADD_BEFORE: 'addBefore',
  CHANGE_TO: 'changeTo',
  DECREASE_BY: 'decreaseBy',
  DELETE: 'delete',
  INCREASE_BY: 'increaseBy',
  SET: 'set',
};

export const BULK_OPERATION_NAME = {
  [BULK_OPERATION.ADD_AFTER]: 'Add after',
  [BULK_OPERATION.ADD_BEFORE]: 'Add before',
  [BULK_OPERATION.CHANGE_TO]: 'Change to',
  [BULK_OPERATION.DECREASE_BY]: 'Decrease by',
  [BULK_OPERATION.DELETE]: 'Delete',
  [BULK_OPERATION.INCREASE_BY]: 'Increase by',
  [BULK_OPERATION.SET]: 'Change to',
};

export const BULK_OPERATIONS = {
  [EBAY]: {
    [ATTRIBUTE.PRICE]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.QUANTITY]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.SKU]: [
      BULK_OPERATION.ADD_BEFORE,
      BULK_OPERATION.ADD_AFTER,
      BULK_OPERATION.DELETE,
      BULK_OPERATION.SET,
    ],
  },
  [ETSY]: {
    [ATTRIBUTE.PRICE]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.QUANTITY]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.SKU]: [
      BULK_OPERATION.ADD_BEFORE,
      BULK_OPERATION.ADD_AFTER,
      BULK_OPERATION.DELETE,
      BULK_OPERATION.SET,
    ],
  },
  [FAIRE]: {
    [ATTRIBUTE.GTIN]: [
      BULK_OPERATION.ADD_BEFORE,
      BULK_OPERATION.ADD_AFTER,
      BULK_OPERATION.DELETE,
      BULK_OPERATION.SET,
    ],
    [ATTRIBUTE.PRICE]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.QUANTITY]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.SKU]: [
      BULK_OPERATION.ADD_BEFORE,
      BULK_OPERATION.ADD_AFTER,
      BULK_OPERATION.DELETE,
      BULK_OPERATION.SET,
    ],
  },
  [SHOPIFY]: {
    [ATTRIBUTE.BARCODE]: [
      BULK_OPERATION.ADD_BEFORE,
      BULK_OPERATION.ADD_AFTER,
      BULK_OPERATION.DELETE,
      BULK_OPERATION.SET,
    ],
    [ATTRIBUTE.PRICE]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.QUANTITY]: [
      BULK_OPERATION.INCREASE_BY,
      BULK_OPERATION.DECREASE_BY,
      BULK_OPERATION.CHANGE_TO,
    ],
    [ATTRIBUTE.SKU]: [
      BULK_OPERATION.ADD_BEFORE,
      BULK_OPERATION.ADD_AFTER,
      BULK_OPERATION.DELETE,
      BULK_OPERATION.SET,
    ],
  },
};
